import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Download, Search, Mail, Building2, User as UserIcon, Calendar, UserPlus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable dialog showing registrations / enrollments for an event or course.
 *
 * Props:
 *   open, onClose
 *   kind: 'event' | 'course'
 *   id: event_id or course_id
 *   title: display title
 */
export default function RegistrationsDialog({ open, onClose, kind, id, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open || !id) { setData(null); setSearch(''); setAddMode(false); setMemberQuery(''); setMemberResults([]); return; }
    const url = kind === 'event'
      ? `/api/admin/events/${id}/registrations`
      : `/api/admin/courses/${id}/enrollments`;
    setLoading(true);
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => toast.error('Failed to load registrations'))
      .finally(() => setLoading(false));
  }, [open, kind, id]);

  // Debounced member search for the "Add member" picker
  useEffect(() => {
    if (!addMode || kind !== 'event') return;
    const q = memberQuery.trim();
    if (q.length < 2) { setMemberResults([]); return; }
    const handle = setTimeout(() => {
      fetch(`/api/members/search?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(rows => {
          // Filter out members already registered
          const already = new Set((data?.registrations || []).map(r => r.user_id));
          setMemberResults(rows.filter(m => !already.has(m.user_id)));
        })
        .catch(() => setMemberResults([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [memberQuery, addMode, kind, data]);

  const handleAddAttendee = async (member) => {
    setAdding(true);
    try {
      const res = await fetch(`/api/admin/events/${id}/add-attendee`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: member.user_id }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`${member.name} added — complimentary badge emailed.`);
      setMemberQuery('');
      setMemberResults([]);
      setAddMode(false);
      // Reload registrations
      const refreshed = await fetch(`/api/admin/events/${id}/registrations`, { credentials: 'include' }).then(r => r.json());
      setData(refreshed);
    } catch (e) { toast.error(e.message || 'Failed'); }
    setAdding(false);
  };

  const rows = data ? (kind === 'event' ? data.registrations : data.enrollments) : [];
  const filtered = rows.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.organisation || '').toLowerCase().includes(q)
    );
  });

  const exportCsv = () => {
    if (!filtered.length) return;
    const isEvent = kind === 'event';
    const headers = isEvent
      ? ['Name', 'Email', 'Organisation', 'Role', 'Mobile', 'Ticket ID', 'Registered At', 'Payment Status']
      : ['Name', 'Email', 'Organisation', 'Role', 'Enrolled At', 'Payment Status', 'Payment Amount', 'Payment Completed', 'Progress %', 'Modules Completed', 'Exam Attempts'];
    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rowsCsv = filtered.map(r => isEvent
      ? [r.name, r.email, r.organisation, r.role, r.mobile_number, r.ticket_id, r.registered_at, r.payment_status].map(escape).join(',')
      : [r.name, r.email, r.organisation, r.role, r.enrolled_at, r.payment_status, r.payment_amount, r.payment_completed_at, r.progress_percent, r.completed_modules, r.exam_attempts].map(escape).join(',')
    );
    const blob = new Blob([headers.join(',') + '\n' + rowsCsv.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-${id}-registrations.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const togglePaymentStatus = async (row) => {
    const nextStatus = row.payment_status === 'paid' || row.payment_status === 'comp' ? 'unpaid' : 'comp';
    const verb = nextStatus === 'comp' ? 'Comp this member' : 'Revert to unpaid';
    if (!(await window.appConfirm(`${verb} for "${row.name}"?`))) return;
    try {
      const res = await fetch(`/api/admin/courses/${id}/enrollments/${row.user_id}/mark-paid`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success(nextStatus === 'comp' ? 'Marked as comp' : 'Reverted to unpaid');
      // Re-fetch
      const refreshed = await fetch(`/api/admin/courses/${id}/enrollments`, { credentials: 'include' }).then(r => r.json());
      setData(refreshed);
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const removeAttendee = async (row) => {
    if (!(await window.appConfirm(
      `Remove "${row.name}" from this event?\n\nThis drops their RSVP and any ticket records. They will not be notified automatically.`
    ))) return;
    try {
      const res = await fetch(`/api/admin/events/${id}/attendees/${row.user_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success(`${row.name} removed from this event`);
      // Optimistic local update — drop the row immediately
      setData(d => d ? ({ ...d, registrations: (d.registrations || []).filter(r => r.user_id !== row.user_id) }) : d);
    } catch (e) {
      toast.error(e.message || 'Failed to remove attendee');
    }
  };

  const sendPaymentReminder = async (userIds = null) => {
    const targetCount = userIds ? userIds.length : (rows.filter(r => r.payment_status === 'unpaid').length);
    if (targetCount === 0) { toast.info('No unpaid enrollees to email.'); return; }
    const noun = userIds && userIds.length === 1 ? 'this member' : `${targetCount} unpaid enrolee${targetCount === 1 ? '' : 's'}`;
    if (!(await window.appConfirm(`Send a payment-reminder email to ${noun}?`))) return;
    try {
      const res = await fetch(`/api/admin/courses/${id}/enrollments/email-payment-reminder`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Origin-URL': window.location.origin },
        body: JSON.stringify(userIds ? { user_ids: userIds } : {}),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Reminder sent to ${d.sent} member${d.sent === 1 ? '' : 's'}${d.failed ? ` · ${d.failed} failed` : ''}${d.skipped ? ` · ${d.skipped} skipped` : ''}`);
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="registrations-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-brand-navy" />
            {kind === 'event' ? 'Registered attendees' : 'Enrolled members'}: {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="registrations-search"
              placeholder="Search by name, email or organisation"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {kind === 'event' && rows.some(r => r.is_ghost) && (
            <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700"
              data-testid="registrations-cleanup-ghosts"
              onClick={async () => {
                const ghostCount = rows.filter(r => r.is_ghost).length;
                if (!(await window.appConfirm(`Remove ${ghostCount} ghost registration${ghostCount === 1 ? '' : 's'} (deleted users still listed as registered)?`))) return;
                try {
                  const res = await fetch(`/api/admin/events/${id}/cleanup-ghost-rsvps`, { method: 'POST', credentials: 'include' });
                  const d = await res.json();
                  if (!res.ok) throw new Error(d.detail || 'Failed');
                  toast.success(`Removed ${d.removed} ghost registration${d.removed === 1 ? '' : 's'}.`);
                  // reload
                  const reload = await fetch(`/api/admin/events/${id}/registrations`, { credentials: 'include' });
                  if (reload.ok) setData(await reload.json());
                } catch (e) { toast.error(e.message || 'Failed'); }
              }}>
              Remove ghosts
            </Button>
          )}
          {kind === 'course' && data?.is_paid_course && rows.some(r => r.payment_status === 'unpaid') && (
            <Button variant="outline" size="sm" onClick={() => sendPaymentReminder()}
              data-testid="bulk-payment-reminder"
              className="text-brand-navy hover:text-brand-dark">
              <Mail className="h-4 w-4 mr-1.5" />
              Remind unpaid ({rows.filter(r => r.payment_status === 'unpaid').length})
            </Button>
          )}
          {kind === 'event' && (
            <Button variant="outline" size="sm"
              data-testid="add-attendee-toggle"
              onClick={() => { setAddMode(v => !v); setMemberQuery(''); setMemberResults([]); }}
              className={addMode ? 'text-brand-navy border-brand-navy' : 'text-brand-navy hover:text-brand-dark'}>
              {addMode ? <><X className="h-4 w-4 mr-1.5" /> Cancel</> : <><UserPlus className="h-4 w-4 mr-1.5" /> Add member</>}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="registrations-export-csv">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>

        {kind === 'event' && addMode && (
          <div className="mb-3 p-3 rounded-lg border-2 border-brand-navy/20 bg-brand-navy/5" data-testid="add-attendee-panel">
            <p className="text-xs font-semibold text-slate-600 mb-2">
              Add a member to this event at no cost — they'll receive a complimentary badge email immediately.
            </p>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                autoFocus
                data-testid="add-attendee-search"
                placeholder="Search by name (min 2 chars)"
                value={memberQuery}
                onChange={e => setMemberQuery(e.target.value)}
                className="pl-9"
                disabled={adding}
              />
            </div>
            {memberQuery.trim().length >= 2 && (
              <div className="mt-2 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg divide-y" data-testid="add-attendee-results">
                {memberResults.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-3 py-3">No new members found.</p>
                ) : memberResults.map(m => (
                  <button
                    key={m.user_id}
                    type="button"
                    disabled={adding}
                    onClick={() => handleAddAttendee(m)}
                    data-testid={`add-attendee-pick-${m.user_id}`}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-brand-navy/5 transition-colors text-left disabled:opacity-50"
                  >
                    <img src={m.picture || `/api/members/${m.user_id}/avatar`} alt="" className="w-7 h-7 rounded-full object-cover" />
                    <span className="text-sm font-medium text-slate-700 flex-1 truncate">{m.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-sage/10 text-brand-sage border border-brand-sage/30">
                      {adding ? 'Adding…' : 'Add (comp)'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="text-xs text-slate-500 mb-2">
          {loading ? 'Loading…' : `${filtered.length} of ${data?.count || 0} ${kind === 'event' ? 'registrations' : 'enrollments'}`}
          {kind === 'event' && data?.max_delegates ? ` · capacity ${data.max_delegates}` : ''}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400">
            {data?.count === 0 ? 'No one has registered yet.' : 'No matches for that search.'}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase border-b border-slate-200">
                  <th className="px-2 py-2 font-medium">Member</th>
                  <th className="px-2 py-2 font-medium">Organisation</th>
                  <th className="px-2 py-2 font-medium">Contact</th>
                  {kind === 'event' ? (
                    <>
                      <th className="px-2 py-2 font-medium">Ticket</th>
                      <th className="px-2 py-2 font-medium">Status</th>
                      <th className="px-2 py-2 font-medium">Registered</th>
                      <th className="px-2 py-2 font-medium w-10"></th>
                    </>
                  ) : (
                    <>
                      <th className="px-2 py-2 font-medium">Enrolled</th>
                      <th className="px-2 py-2 font-medium">Payment</th>
                      <th className="px-2 py-2 font-medium">Progress</th>
                      <th className="px-2 py-2 font-medium">Exam</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => (
                  <tr key={r.user_id || idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`registration-row-${r.user_id}`}>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                          {r.picture && <img src={r.picture} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{r.name}</div>
                          {r.role && <div className="text-[11px] text-slate-500 truncate">{r.role}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-slate-600">
                      {r.organisation ? (
                        <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-slate-400" /> {r.organisation}</span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-2.5">
                      {r.email && (
                        <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-brand-navy hover:underline">
                          <Mail className="h-3.5 w-3.5" /> {r.email}
                        </a>
                      )}
                      {kind === 'event' && r.mobile_number && (
                        <div className="text-[11px] text-slate-500 mt-0.5">{r.mobile_number}</div>
                      )}
                    </td>
                    {kind === 'event' ? (
                      <>
                        <td className="px-2 py-2.5 font-mono text-[11px] text-slate-500">{r.ticket_id || '—'}</td>
                        <td className="px-2 py-2.5">
                          {r.payment_status === 'paid' ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Paid</span>
                          ) : r.payment_status === 'pending' ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Payment pending</span>
                          ) : r.payment_status === 'free' ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-100">Free</span>
                          ) : r.payment_status === 'comp' ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">Comp</span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.payment_status || '—'}</span>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-slate-400" /> {fmtDate(r.registered_at)}</span>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => removeAttendee(r)}
                            title="Remove this attendee from the event"
                            data-testid={`remove-attendee-${r.user_id}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-2 py-2.5 text-slate-600 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-slate-400" /> {fmtDate(r.enrolled_at)}</span>
                        </td>
                        <td className="px-2 py-2.5">
                          {(() => {
                            const ps = r.payment_status;
                            const pill = ps === 'paid' ? { txt: r.payment_amount ? `Paid · £${r.payment_amount}` : 'Paid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' }
                              : ps === 'comp' ? { txt: 'Comp', cls: 'bg-indigo-50 text-indigo-700 border-indigo-100' }
                              : ps === 'unpaid' ? { txt: 'Unpaid', cls: 'bg-rose-50 text-rose-700 border-rose-200' }
                              : ps === 'free' ? { txt: 'Free', cls: 'bg-slate-50 text-slate-600 border-slate-100' }
                              : { txt: ps || '—', cls: 'bg-slate-100 text-slate-500 border-slate-100' };
                            return (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${pill.cls}`} data-testid={`payment-pill-${r.user_id}`}>{pill.txt}</span>
                                {data?.is_paid_course && ps !== 'free' && (
                                  <button
                                    type="button"
                                    onClick={() => togglePaymentStatus(r)}
                                    data-testid={`toggle-payment-${r.user_id}`}
                                    className="text-[10px] text-slate-500 hover:text-brand-navy underline"
                                  >
                                    {ps === 'comp' || ps === 'paid' ? 'Mark unpaid' : 'Mark as comp'}
                                  </button>
                                )}
                                {data?.is_paid_course && ps === 'unpaid' && (
                                  <button
                                    type="button"
                                    onClick={() => sendPaymentReminder([r.user_id])}
                                    data-testid={`remind-payment-${r.user_id}`}
                                    className="text-[10px] text-brand-navy hover:text-brand-dark underline inline-flex items-center gap-0.5"
                                    title="Email this member a payment-link reminder"
                                  >
                                    <Mail className="h-2.5 w-2.5" /> Remind
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          {r.payment_completed_at && (
                            <div className="text-[10px] text-slate-400 mt-0.5">{fmtDate(r.payment_completed_at)}</div>
                          )}
                        </td>
                        <td className="px-2 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-brand-sage" style={{ width: `${Math.min(100, r.progress_percent || 0)}%` }} />
                            </div>
                            <span className="text-[11px] text-slate-500 tabular-nums">{r.progress_percent || 0}%</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{r.completed_modules} module{r.completed_modules === 1 ? '' : 's'} done</div>
                        </td>
                        <td className="px-2 py-2.5 text-slate-600 whitespace-nowrap">
                          {r.exam_attempts > 0
                            ? <span>{r.exam_attempts} attempt{r.exam_attempts === 1 ? '' : 's'}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
